<template>
  <div>
    <div class="password-policy-notice">
      <p>
        <strong>Hinweis: Unsere Passwort-Richtlinien wurden aktualisiert, um Ihre Daten noch besser zu schützen.</strong>
        Sollten Sie Ihr Passwort noch nicht angepasst haben, leiten wir Sie nach dem Login automatisch zur Passwortänderung weiter. Wenn Sie Ihr Passwort bereits geändert haben, müssen Sie nichts weiter tun.
      </p>
    </div>

    <c-form @submit.prevent="submit">

      <c-title>Login</c-title>

      <c-form-input
        id="email"
        :errors="[{
          message: 'Dieses Feld ist erforderlich.',
          show: !$v.email.required
        }, {
          message: 'Keine gültige E-Mail-Adresse.',
          show: !$v.email.email
        }]"
        :has-error="$v.email.$error"
        v-model="email"
        aria-required="true"
        autocomplete="username"
        label="E-Mail-Adresse"
        type="email"
      />

      <c-form-input
        id="password"
        :errors="[{
          message: 'Dieses Feld ist erforderlich.',
          show: !$v.password.required
        }]"
        :has-error="$v.password.$error"
        v-model="password"
        aria-required="true"
        autocomplete="current-password"
        label="Passwort"
        type="password"
      />

      <c-friendly-captcha
        ref="captcha"
        site-key="FCMSDF99SJRQIVO9"
        @solved="onCaptchaSolved"
        @error="onCaptchaError"
      />

      <c-button :busy="loginStatus === $config.status.LOADING">
        Anmelden
      </c-button>

      <router-link :to="{ name: 'signup' }">
        Neues Konto erstellen
      </router-link>

      <router-link :to="{ name: 'reset-password' }">
        Passwort vergessen
      </router-link>

    </c-form>

    <p class="ie-notice">
      Arbeiten Sie mit dem Internet Explorer?
      Dieser wird von <a
        href="https://techcommunity.microsoft.com/t5/Windows-IT-Pro-Blog/The-perils-of-using-Internet-Explorer-as-your-default-browser/ba-p/331732"
        target="_blank"
      >Microsoft</a>
      seit 2016 nicht mehr weiterentwickelt und entspricht nicht mehr den aktuellen technischen und sicherheitsrelevanten Anforderungen.
      Wechseln Sie deshalb bitte auf einen anderen Browser, zum Beispiel Google Chrome oder Mozilla Firefox.
    </p>
  </div>
</template>

<script>
import CButton from '@/components/CButton'
import CForm from '@/components/form/CForm'
import CFormInput from '@/components/form/CFormInput'
import CTitle from '@/components/CTitle'
import CFriendlyCaptcha from '@/components/CFriendlyCaptcha'

import handleRequestError from '@/functions/handleRequestError'
import sessionToken from '@/functions/sessionToken'
import { email, required } from 'vuelidate/lib/validators'

export default {
  name: 'VLogin',

  components: {
    CButton,
    CForm,
    CFormInput,
    CTitle,
    CFriendlyCaptcha
  },

  props: {
    token: {
      type: String
    }
  },

  data () {
    return {
      loginStatus: this.$config.status.IDLE,
      email: '',
      password: '',
      captchaSolution: ''
    }
  },

  validations: {
    email: {
      email,
      required
    },
    password: {
      required
    }
  },
  created () {
    if (sessionToken.get() && this.token) {
      this.activateTeacherStaffAccount()
    }
  },

  methods: {
    activateTeacherStaffAccount () {
      this.$api.patch(`projects/registration-token/${this.token}/`, {
        identifier: this.token
      })
        .then(response => {
          this.$store.commit('notifications/add', {
            type: 'success',
            text: 'Sie wurden erfolgreich freigeschaltet.'
          })
          this.$router.push({ name: 'dashboard' })
        })
        .catch(error => {
          handleRequestError({
            error,
            fallback: 'Es ist ein Fehler bei der Freischaltung aufgetreten. Bitte versuchen Sie es später erneut.'
          })
        })
    },
    onCaptchaSolved (solution) {
      this.captchaSolution = solution
    },

    onCaptchaError (error) {
      this.$store.commit('notifications/add', {
        text: 'Captcha-Fehler: ' + error,
        type: 'error'
      })
    },

    submit () {
      this.$v.$touch()

      if (this.$v.$invalid) {
        this.$store.commit('notifications/add', {
          text: 'Bitte füllen Sie alle Felder korrekt aus.',
          type: 'error'
        })

        return
      }

      if (!this.captchaSolution) {
        this.$store.commit('notifications/add', {
          text: 'Bitte lösen Sie das Captcha.',
          type: 'error'
        })
        return
      }

      this.loginStatus = this.$config.status.LOADING

      this.$api.post('auth/login/', {
        captcha: this.captchaSolution
      }, {
        auth: {
          username: this.email,
          password: this.password
        }
      })
        .then(response => {
          sessionToken.set(response.data.token)
          if (this.token) {
            this.activateTeacherStaffAccount()
          }
          return this.$api.get('auth/user/')
        })
        .then(response => {
          this.$store.commit('user/data', response.data)
          return this.$nextTick()
        })
        .then(() => {
          if (!this.$store.state.user.data.password_updated_to_new_policy) {
            this.$router.push({ name: 'change-password' })
          } else if (this.$store.state.user.data.selected_role) {
            this.$router.push({ name: 'dashboard' })
          } else {
            this.$router.push({ name: 'select-role' })
          }
        })
        .catch(error => {
          sessionToken.remove()

          if (error.response.data.code === 'unverified') {
            this.$router.push({ name: 'unverified-account' })
          }

          if (error.response.status === 403) {
            if(error.response.data["detail"].includes("Captcha"))
            {
              this.$store.commit('notifications/add', {
                text: 'Die Anmeldung war nicht erfolgreich. Das Captcha ist ungültig.',
                type: 'error',
                timeout: 10000
              })
              if (this.$refs.captcha) {
                this.$refs.captcha.reset()
                this.captchaSolution = ''
              }
            } else {
              this.$store.commit('notifications/add', {
                text: 'Die Anmeldung war nicht erfolgreich. Bitte überprüfen Sie Ihren Benutzernamen und Ihr Passwort. Hinweis: Zum Schutz Ihrer Daten haben wir unsere Passwort-Richtlinien aktualisiert. Möglicherweise ist eine einmalige Passwortänderung erforderlich.',
                type: 'error',
                timeout: 10000
              })
            }
          }
          else if(error.response.status === 400) {
            this.$store.commit('notifications/add', {
              text: 'Die Anmeldung war nicht erfolgreich. Das Captcha ist ungültig.',
              type: 'error',
              timeout: 10000
            })

            if (this.$refs.captcha) {
              this.$refs.captcha.reset()
              this.captchaSolution = ''
            }
          }
          this.loginStatus = this.$config.status.ERROR
        })
    }
  }
}
</script>

<style scoped>
.password-policy-notice {
  background-color: #e8f4fd;
  border: 1px solid #bee5eb;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 20px;
}

.password-policy-notice p {
  margin: 0;
  color: #0c5460;
  font-size: 14px;
  line-height: 1.5;
}

.ie-notice {
  margin-top: 20px;
}
</style>
